import { FieldError, InfoPopover, Select, TextField } from '@automata/ui';
import { ComparisonInput } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import startCase from 'lodash/startCase';
import { FC, useCallback, useEffect } from 'react';

import {
  convertValueToType,
  getManualValueType,
  getSourceDataType,
} from './utils';
import { ParameterType } from '../../../../components/GraphEditorToolbars/ParametersToolbar/types';
import { selectParameterDefinitions } from '../../../../features/workflow/selectors';
import { selectDataDependencies } from '../../../../features/workflowJsonSelectors';
import { useAppSelector } from '../../../../hooks';
import { ConditionPropertiesForm } from '../../../../zodSchemas/conditionProperties';
import { TextFieldWithMaxLength } from '../../../TextFieldWithMaxLength';
import { ExposingParameterChip } from '../ExposingParameterChip';
import { ExposingParameterPopover } from '../ExposingParameterPopover/ExposingParameterPopover';
import { FormSectionLabel } from '../FormSectionLabel';

const styles = {
  card: {
    backgroundColor: 'grey.200',
    boxShadow: 'none',
    padding: '8px',
    marginTop: '-8px',
  },
  cardContent: { padding: '0' },
};

export type LogicalFieldsProps = {
  id: string;
  values: ConditionPropertiesForm;
  updateTaskProperties: (formUpdate: Partial<ConditionPropertiesForm>) => void;
};

enum Operator {
  Equal = '==',
  NotEqual = '!=',
  In = 'in',
  NotIn = 'not in',
}

enum NumberOperator {
  GreaterThan = '>',
  GreaterOrEqual = '>=',
  LessThan = '<',
  LessOrEqual = '<=',
}

const fieldName = 'sourceValue';

export const LogicalFields: FC<LogicalFieldsProps> = ({
  id,
  values,
  updateTaskProperties,
}) => {
  const sourceData = useAppSelector(selectDataDependencies)[id]?.source_data;
  const parameterDefinitions = useAppSelector(selectParameterDefinitions);

  const referenceSourceValue =
    'key' in values.comparison.lhs ? values.comparison.lhs.key : '';

  const dataSourceValue =
    'output_name' in values.comparison.lhs
      ? (values.comparison.lhs.output_name as string)
      : '';

  const sourceValue = startCase(referenceSourceValue || dataSourceValue);

  const targetValue =
    'value' in values.comparison.rhs
      ? (values.comparison.rhs.value as string)
      : '';

  const parameterReference = parameterDefinitions.find(
    (p) => p.id === referenceSourceValue
  );

  const externalSourceType: string | null =
    sourceData && 'type' in sourceData ? sourceData.type ?? null : null;

  const sourceDataType = getSourceDataType(
    parameterReference,
    externalSourceType,
    referenceSourceValue
  );

  const parameterAppliedValue = parameterReference
    ? String(parameterReference.default ?? '')
    : sourceValue && `${sourceValue} (${sourceDataType})`;

  const handleLabelChange = useCallback(
    (labelName: string, value: string) => {
      updateTaskProperties({ [labelName]: value });
    },
    [updateTaskProperties]
  );

  const handleTargetValueChange = (value: string) => {
    const convertedValue = convertValueToType(value, sourceDataType);
    updateTaskProperties({
      comparison: {
        ...values.comparison,
        rhs: { value: convertedValue },
      },
    });
  };

  useEffect(() => {
    let expectedData = values.comparison.lhs;

    if (sourceData && 'output_name' in sourceData) {
      expectedData = {
        task_id: sourceData.task_id,
        output_name: sourceData.output_name,
      };
    }
    if (!sourceData) {
      expectedData = {
        key: parameterReference?.id || '',
      };
    }

    if (
      expectedData &&
      (('output_name' in expectedData &&
        sourceValue !== startCase(expectedData.output_name ?? '')) ||
        ('key' in expectedData && sourceValue !== startCase(expectedData.key)))
    ) {
      updateTaskProperties({
        comparison: { ...values.comparison, lhs: expectedData },
      });
    }
  }, [
    parameterReference?.id,
    sourceData,
    sourceValue,
    values.comparison,
    updateTaskProperties,
  ]);

  const operators =
    sourceDataType === 'number'
      ? { ...Operator, ...NumberOperator }
      : { ...Operator };

  const targetDataType = getManualValueType(targetValue);

  const error =
    sourceDataType !== 'string' && sourceDataType !== targetDataType
      ? `Target data type should be ${sourceDataType}`
      : null;

  return (
    <Stack gap={1}>
      <Box mt={2}>
        <FormSectionLabel>Settings</FormSectionLabel>
      </Box>

      <FormControl fullWidth>
        <Select
          infoPopover={<InfoPopover copy="preferred condition to plan text" />}
          infoPopoverAnchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          infoPopoverTransformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          label="preferred condition to plan"
          value={values.expectedOutcome}
          onChange={(e) => {
            updateTaskProperties({
              expectedOutcome: e.target.value as
                | 'IF condition'
                | 'ELSE condition',
            });
          }}
        >
          {['IF condition', 'ELSE condition'].map((item) => (
            <MenuItem key={item} value={item}>
              {startCase(item)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box mt={2}>
        <FormSectionLabel>IF condition</FormSectionLabel>
      </Box>

      <FormControl fullWidth>
        <TextFieldWithMaxLength
          error={[].length > 0} // todo (p.karpovich): here and below the validation will be added in the next stories
          infoPopover={<InfoPopover copy="Condition Name text" />} // todo (p.karpovich): should be requested from Joao and updated here in other places in the component
          infoPopoverAnchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          infoPopoverTransformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          label="Condition Name"
          maxLength={25}
          value={values.ifLabel || ''}
          onChange={(e) => handleLabelChange('ifLabel', e.target.value)}
        />
        <FieldError error={[]} />
      </FormControl>

      <Card sx={styles.card}>
        <CardContent sx={styles.cardContent}>
          <Stack spacing={1} alignItems="center">
            <FormControl fullWidth>
              <Stack gap={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-end"
                  width="100%"
                >
                  <TextField
                    disabled={true}
                    label="Source value"
                    placeholder="( no data connected )"
                    value={parameterAppliedValue}
                    onChange={(e) =>
                      handleLabelChange('sourceValue', e.target.value)
                    }
                  />

                  {!sourceValue && (
                    <ExposingParameterPopover
                      appliedParameterId={referenceSourceValue}
                      parameterName="sourceValue"
                      taskId={id}
                      types={[
                        ParameterType.STRING,
                        ParameterType.FLOAT,
                        ParameterType.INTEGER,
                        ParameterType.BOOLEAN,
                      ]}
                    />
                  )}
                </Stack>
                {parameterReference && (
                  <ExposingParameterChip
                    parameter={parameterReference}
                    fieldName={fieldName}
                    taskId={id}
                  />
                )}
              </Stack>
            </FormControl>

            <FormControl fullWidth>
              <Select
                label="operator"
                value={values.comparison.operator}
                onChange={(e) =>
                  updateTaskProperties({
                    comparison: {
                      ...values.comparison,
                      operator: e.target.value as ComparisonInput['operator'],
                    },
                  })
                }
              >
                {Object.entries(operators).map(([key, value]) => (
                  <MenuItem key={value} value={value}>
                    {startCase(key)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <TextField
                error={!!error}
                label="Target value"
                value={targetValue}
                onChange={(e) => handleTargetValueChange(e.target.value)}
              />
              <FieldError error={error} />
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      <Box mt={2}>
        <FormSectionLabel>Else condition</FormSectionLabel>
      </Box>

      <FormControl fullWidth>
        <TextFieldWithMaxLength
          error={[].length > 0}
          infoPopover={<InfoPopover copy="Condition Name text" />}
          infoPopoverAnchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          infoPopoverTransformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          label="Condition Name"
          maxLength={25}
          value={values.elseLabel}
          onChange={(e) => handleLabelChange('elseLabel', e.target.value)}
        />
        <FieldError error={[]} />
      </FormControl>
    </Stack>
  );
};
